import { Switch, Input, Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { IInvoiceResult } from "../../../interfaces/invoice";
import { useMessageApi } from "../../../context/MessageContext";

interface AiLimitationCellProps {
  record: IInvoiceResult;
  onSave: (id: number, value: { aiLimitation: boolean; aiLimitationReason: string }) => Promise<void>;
}

const AiLimitationCell: React.FC<AiLimitationCellProps> = ({ record, onSave }) => {
  const [checked, setChecked] = useState(record.aiLimitation);
  const [reason, setReason] = useState(record.aiLimitationReason || "");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const messageApi = useMessageApi();

  const textAreaRef = useRef<any>(null);

  const initialCheckedRef = useRef(record.aiLimitation ?? false);
  const initialReasonRef = useRef(record.aiLimitationReason ?? "");

  useEffect(() => {
    if (editing && textAreaRef.current) {
      const textarea = textAreaRef.current?.resizableTextArea?.textArea;

      if (textarea) {
        textarea.focus();
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);
      }
    }
  }, [editing]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const oldCheckedVal = initialCheckedRef.current ?? false;
      const newCheckedVal = checked ?? false;

      const oldVal = initialReasonRef.current ?? "";
      const newVal = reason ?? "";
      if (oldCheckedVal !== newCheckedVal || oldVal !== newVal) {
        await onSave(Number(record.id), { aiLimitation: checked ?? false, aiLimitationReason: reason });
        initialCheckedRef.current = newCheckedVal;
        initialReasonRef.current = newVal;
        messageApi.success("Saved!");
      }
      setEditing(false);
    } catch (err) {
      messageApi.error("Save failed!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 4,
        }}
      >
        <Switch
          checked={checked}
          onChange={(val) => setChecked(val)}
          loading={loading}
          onClick={() => setEditing(true)}
          style={{ height: 24, width: 40 }}
        />

        {editing ? (
          <Input.TextArea
            ref={textAreaRef}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={handleSave}
            onPressEnter={handleSave}
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ fontSize: 12, width: "100%" }}
          />
        ) : (
          <Tooltip title={reason || "No reason"} placement="topLeft">
            <div
              style={{
                minHeight: 24,
                padding: "2px 4px",
                border: "1px dashed #ccc",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 12,
                color: reason ? "inherit" : "#999",
                width: "100%",
              }}
              onClick={() => setEditing(true)}
            >
              {reason || "Click to edit"}
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default AiLimitationCell;
